import axios from "axios";
import { ProjectPaymentDetail } from "../components/projectExpense/types";
import { getValidToken } from "../../../utils/authUtils";
import { Vehicle } from "../../../services/api";
import { VehicleExpenseData } from "../../../pages/vehicleExpense/apiService";

interface ApiResponse<T> {
  data: T;
  message?: string;
  isSuccess: boolean;
}

// API request/response types based on the documentation
export interface ProjectExpenseData {
  projectId: string;
  vehicleId?: string;
  expenseDate: string;
  expenseType:
    | "FUEL"
    | "REPAIR"
    | "DRIVER_CLEANER"
    | "PARKING"
    | "OTHER"
    | "INSURANCE"
    | "CHALLAN";
  amount: number;
  paidTo: string;
  location: string;
  contactNumber?: string;
  notes?: string;
  description?: string;
  gst?: number;
}

export interface FuelExpenseDetail {
  fuelType: "Petrol" | "Diesel" | "CNG" | "Electric";
  liters: number;
  pricePerLitre: number;
  bulkDiscountApplied?: number;
  petrolPumpId?: string;
  odometerReading?: number;
}

export interface MaintenanceExpenseDetail {
  serviceType?: string;
  serviceProviderType?: "Garage" | "Mechanic";
  serviceProviderId?: string;
  maintenanceItem?: string;
  customMaintenanceItem?: string;
  actionType?:
    | "Repaired"
    | "Replaced"
    | "Serviced"
    | "Inspected"
    | "Adjusted"
    | "Cleaned";
  warrantyPeriod?: string;
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  customWarrantyPeriod?: string;
  nextServiceDue?: number;
  invoiceNumber?: string;
  workOrderNumber?: string;
  odometerReading?: number;
}

export interface StaffParkingExpenseDetail {
  category:
    | "DRIVER"
    | "CLEANER"
    | "PARKING"
    | "TOLL"
    | "RTO"
    | "Challan"
    | "Food Allowance"
    | "Mobile Recharge"
    | "Loading/Unloading Fees"
    | "Night Halt Charges"
    | "Emergency Fund Used"
    | "Dhobi"
    | "Bus Cleaning (Interior)"
    | "Custom";
  referenceId?: string;
  customCategory?: string;
}

export interface ChallanExpenseDetail {
  responsibleParty: "DRIVER" | "OFFICE";
  responsiblePartyName?: string;
  responsiblePartyContact?: string;
  challanType?: string;
  challanNumber?: string;
  challanAction?: string;
}

export interface CreateExpenseRequest {
  expenseData: VehicleExpenseData;
  detailData:
    | FuelExpenseDetail
    | MaintenanceExpenseDetail
    | StaffParkingExpenseDetail
    | ChallanExpenseDetail;
  paymentData?: Array<ProjectPaymentDetail>;
}

export interface ProjectExpenseResponse {
  id: string;
  vehicleId?: string;
  vehicle?: Vehicle;
  expenseDate: string;
  expenseType: string;
  amount: number;
  paidTo: string;
  location: string;
  contactNumber?: string;
  notes?: string;
  description?: string;
  gst?: number;
  createdAt: string;
  updatedAt: string;
  details?: any;
  payments?: ProjectPaymentDetail[];
  paidAmount?: number;
  balanceAmount?: number;
  masterReferenceId?: string | null;
  masterCategory?: string | null;
  companyId?: string | null;
  createdBy?: string | null;
}

// Extended interface for detailed expense response (used in edit mode)
export interface ProjectExpenseDetailResponse extends ProjectExpenseResponse {
  payments?: ProjectPaymentDetail[];
}

export interface ExpenseListResponse {
  expenses: ProjectExpenseResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ExpenseSummary {
  expenseType: string;
  count: string;
  totalAmount: string;
  averageAmount: string;
}

export interface FuelEfficiencyData {
  totalExpenses: number;
  totalLiters: string;
  totalAmount: string;
  averagePricePerLiter: string;
  expenses: ProjectExpenseResponse[];
}

export const projectExpenseApi = {
  fetchVehicles: async (): Promise<Vehicle[]> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}vehicles`;
      const response = await axios.get<ApiResponse<Vehicle[]>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      return [];
    }
  },
  // Create a new expense with details
  createExpense: async (
    projectId: string,
    expenseRequest: CreateExpenseRequest
  ): Promise<ProjectExpenseResponse> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/v2`;
      const response = await axios.post<ApiResponse<ProjectExpenseResponse>>(
        url,
        expenseRequest,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to create expense");
    } catch (error: any) {
      console.error("Error creating expense:", error);
      throw new Error(
        error.response?.data?.message || "Failed to create expense"
      );
    }
  },

  // Get all expenses with pagination and filters
  getExpenses: async (
    projectId: string,
    params: any
  ): Promise<any> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/v2`;
      const response = await axios.get<ApiResponse<ExpenseListResponse>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to fetch expenses");
    } catch (error: any) {
      console.error("Error fetching expenses:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch expenses"
      );
    }
  },

  // Get expense by ID
  getExpenseById: async (
    projectId: string,
    expenseId: string
  ): Promise<ProjectExpenseDetailResponse> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/v2/${expenseId}`;
      const response = await axios.get<
        ApiResponse<ProjectExpenseDetailResponse>
      >(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to fetch expense");
    } catch (error: any) {
      console.error("Error fetching expense:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch expense"
      );
    }
  },

  // Update expense
  updateExpense: async (
    projectId: string,
    expenseId: string,
    expenseRequest: CreateExpenseRequest
  ): Promise<ProjectExpenseResponse> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/v2/${expenseId}`;
      const response = await axios.put<ApiResponse<ProjectExpenseResponse>>(
        url,
        expenseRequest,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to update expense");
    } catch (error: any) {
      console.error("Error updating expense:", error);
      throw new Error(
        error.response?.data?.message || "Failed to update expense"
      );
    }
  },

  // Delete expense
  deleteExpense: async (
    projectId: string,
    expenseId: string
  ): Promise<boolean> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/v2/${expenseId}`;
      const response = await axios.delete<ApiResponse<boolean>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to delete expense");
    } catch (error: any) {
      console.error("Error deleting expense:", error);
      throw new Error(
        error.response?.data?.message || "Failed to delete expense"
      );
    }
  },

  // Bulk create expenses
  bulkCreateExpenses: async (
    projectId: string,
    expenseRequests: CreateExpenseRequest[]
  ): Promise<ProjectExpenseResponse[]> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/v2/bulk`;
      const response = await axios.post<ApiResponse<ProjectExpenseResponse[]>>(
        url,
        { expenses: expenseRequests },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to create expenses");
    } catch (error: any) {
      console.error("Error creating expenses:", error);
      throw new Error(
        error.response?.data?.message || "Failed to create expenses"
      );
    }
  },

  // Create payments for an expense
  createPayments: async (
    projectId: string,
    expenseId: string,
    payments: ProjectPaymentDetail[]
  ): Promise<ProjectPaymentDetail[]> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/v2/payments`;
      const response = await axios.post<ApiResponse<ProjectPaymentDetail[]>>(
        url,
        { expenseId, payments },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to create payments");
    } catch (error: any) {
      console.error("Error creating payments:", error);
      throw new Error(
        error.response?.data?.message || "Failed to create payments"
      );
    }
  },

  // Delete payments
  deletePayments: async (
    projectId: string,
    paymentIds: string[]
  ): Promise<boolean> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/v2/payments/delete`;
      const response = await axios.put<ApiResponse<boolean>>(
        url,
        { paymentIds },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(response.data?.message || "Failed to delete payments");
    } catch (error: any) {
      console.error("Error deleting payments:", error);
      throw new Error(
        error.response?.data?.message || "Failed to delete payments"
      );
    }
  },

  // Get expenses by vehicle
  getExpensesByVehicle: async (
    projectId: string,
    vehicleId: string,
    params?: any
  ): Promise<ExpenseListResponse> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/vehicle/${vehicleId}`;
      const response = await axios.get<ApiResponse<ExpenseListResponse>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(
        response.data?.message || "Failed to fetch vehicle expenses"
      );
    } catch (error: any) {
      console.error("Error fetching vehicle expenses:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch vehicle expenses"
      );
    }
  },

  // Get expense summary by vehicle
  getExpenseSummaryByVehicle: async (
    projectId: string,
    vehicleId: string
  ): Promise<ExpenseSummary[]> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/vehicle/${vehicleId}/summary`;
      const response = await axios.get<ApiResponse<ExpenseSummary[]>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(
        response.data?.message || "Failed to fetch expense summary"
      );
    } catch (error: any) {
      console.error("Error fetching expense summary:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch expense summary"
      );
    }
  },

  // Get expenses by date range
  getExpensesByDateRange: async (
    projectId: string,
    startDate: string,
    endDate: string,
    params?: any
  ): Promise<ExpenseListResponse> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/date-range`;
      const response = await axios.get<ApiResponse<ExpenseListResponse>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { startDate, endDate, ...params },
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(
        response.data?.message || "Failed to fetch expenses by date range"
      );
    } catch (error: any) {
      console.error("Error fetching expenses by date range:", error);
      throw new Error(
        error.response?.data?.message ||
          "Failed to fetch expenses by date range"
      );
    }
  },

  // Get expense statistics
  getExpenseStatistics: async (projectId: string): Promise<any> => {
    const token = await getValidToken();
    try {
      const url = `${process.env.REACT_APP_API_HOST}project-module/projects/${projectId}/vehicle-expenses/statistics`;
      const response = await axios.get<ApiResponse<any>>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data?.isSuccess) {
        return response.data.data;
      }
      throw new Error(
        response.data?.message || "Failed to fetch expense statistics"
      );
    } catch (error: any) {
      console.error("Error fetching expense statistics:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch expense statistics"
      );
    }
  },
};
